import useSessionStore from '@/stores/useSessionStore'
import { loadScript } from '@/utils/loadScript'

import useExchangeGoogleToken, { GoogleTokenExchangeResponse } from '../api/useExchangeGoogleToken'

const GOOGLE_CLIENT_ID = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID
const GOOGLE_SCRIPT_ID = 'google-gsi-client'
const GOOGLE_SCRIPT_SRC = 'https://accounts.google.com/gsi/client'

const ensureGoogleSdk = (): Promise<void> =>
  loadScript({
    id: GOOGLE_SCRIPT_ID,
    src: GOOGLE_SCRIPT_SRC,
  })

export const useGoogleAuth = (
  onSuccessCallback?: (response: GoogleTokenExchangeResponse) => void,
): {
  initiateGoogleAuth: () => void
  googleTokenExchangeError: Error | null
} => {
  const { mutate: exchangeGoogleToken, error: googleTokenExchangeError } = useExchangeGoogleToken()
  const setAuth = useSessionStore((store) => store.setAuth)

  /**
   * Handles the Google OAuth response by sending the access token to our
   * Rails backend API for verification and user authentication.
   */
  const handleGoogleResponse = ({ access_token }: { access_token: string }): void => {
    exchangeGoogleToken(
      { access_token, frontend_app: true },
      {
        // If useGoogleAuth response is successful, we have the x-user-token
        // so set it to the store for reactive changes elsewhere
        onSuccess: (response) => {
          setAuth(response.auth_token ?? '', response.user_id, response.user_email ?? '')
          onSuccessCallback?.(response)
        },
      },
    )
  }

  /**
   * Initiates the Google OAuth popup flow
   */
  const initiateGoogleAuth = (): void => {
    const scope = `https://www.googleapis.com/auth/userinfo.profile \
                   https://www.googleapis.com/auth/userinfo.email`

    void ensureGoogleSdk().then(() => {
      if (typeof window !== 'undefined' && window.google?.accounts) {
        const tokenClient = window.google.accounts.oauth2.initTokenClient({
          client_id: GOOGLE_CLIENT_ID || '',
          ux_mode: 'popup',
          response_type: 'id_token',
          scope,
          callback: handleGoogleResponse,
        })

        tokenClient.requestAccessToken()
      }
    })
  }

  return {
    initiateGoogleAuth,
    googleTokenExchangeError,
  }
}
